import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  CheckCircle,
  XCircle,
  Loader2,
  Mail,
  ArrowRight,
  RefreshCw,
  Shield,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../lib/supabaseConfig";
import { useAuth } from "../context/AuthContext";
import gsap from "gsap";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("verifying"); // verifying, success, error, expired
  const [message, setMessage] = useState(
    "Processing your email verification..."
  );
  const [isResending, setIsResending] = useState(false);
  const [redirectTo, setRedirectTo] = useState("/login"); // Track where to redirect
  const [gridDimensions, setGridDimensions] = useState({ rows: 0, cols: 0 });
  const navigate = useNavigate();
  const { user } = useAuth();

  // Animation refs
  const containerRef = useRef(null);
  const iconRef = useRef(null);
  const contentRef = useRef(null);

  // Set grid dimensions
  useEffect(() => {
    const updateGridDimensions = () => {
      setGridDimensions({
        rows: Math.ceil(window.innerHeight / 40),
        cols: Math.ceil(window.innerWidth / 40),
      });
    };

    updateGridDimensions();
    window.addEventListener("resize", updateGridDimensions);

    return () => window.removeEventListener("resize", updateGridDimensions);
  }, []);

  // Verify email token
  useEffect(() => {
    const token = searchParams.get("token");
    console.log(
      "🔍 VerifyEmail component mounted, token:",
      token ? "present" : "missing"
    );

    if (!token) {
      console.log("❌ No token found in URL");
      setStatus("error");
      setMessage(
        "Invalid verification link. No token provided. Please check the link in your email."
      );
      return;
    }

    // Add a small delay to ensure the component is fully rendered
    const timeoutId = setTimeout(() => {
      console.log("🚀 Starting verification process...");
      verifyEmailToken(token);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [searchParams]);

  // GSAP Animations
  useEffect(() => {
    if (containerRef.current && iconRef.current && contentRef.current) {
      const tl = gsap.timeline();

      // Initial setup
      gsap.set([iconRef.current, contentRef.current], {
        opacity: 0,
        y: 30,
      });

      // Entrance animation
      tl.to(iconRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "back.out(1.7)",
      }).to(
        contentRef.current,
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: "power2.out",
        },
        "-=0.3"
      );

      // Success animation
      if (status === "success" && iconRef.current) {
        gsap.to(iconRef.current, {
          scale: 1.1,
          duration: 0.3,
          yoyo: true,
          repeat: 1,
          ease: "power2.inOut",
          delay: 0.5,
        });
      }
    }
  }, [status]);

  const verifyEmailToken = async (token) => {
    // Step 1: Verify the token
    try {
      setStatus("verifying");
      console.log("🔍 Full verification token:", token);
      console.log("🔍 Token length:", token?.length);
      console.log(
        "🔍 Starting verification for token:",
        token.substring(0, 10) + "..."
      );

      // Update UI to show we're processing
      setMessage("Verifying your email token...");

      // Call the database function to verify email
      const { data, error } = await supabase.rpc("verify_email", {
        verification_token: token,
      });

      console.log("🔄 Full verification response:", { data, error });

      if (error) {
        console.error("❌ Database function error details:", {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });

        // Check if it's a function not found error
        if (
          error.message?.includes("function") &&
          error.message?.includes("does not exist")
        ) {
          setStatus("error");
          setMessage(
            "Database verification function not found. Please contact support."
          );
          return;
        }

        throw error;
      }

      console.log("🔄 Database response data:", data);
      console.log("🔄 Data success value:", data?.success);
      console.log("🔄 Data error value:", data?.error);

      if (!data?.success) {
        console.log("❌ Verification failed, data:", data);
        setStatus("error");
        setMessage(
          data?.error ||
            "Verification failed. The token may be invalid or expired."
        );
        return;
      }

      // Step 2: Verification succeeded - this status should never change
      setStatus("success");
      console.log("✅ Email verification successful!");
    } catch (error) {
      console.error("Verification error:", error);
      setStatus("error");

      // More detailed error messages
      if (error.message?.includes("Invalid or expired")) {
        setMessage(
          "This verification link has expired or is invalid. Please request a new verification email by trying to log in to your account."
        );
      } else {
        setMessage(
          `Verification failed: ${error.message || "An unexpected error occurred"}. Please try again or contact support.`
        );
      }
      return;
    }

    // Step 3: Handle post-verification redirect (errors here don't affect verification status)
    try {
      setMessage(
        "Your email has been verified successfully! You can now access your account."
      );

      // Check if user is still authenticated
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        // User is authenticated, redirect to homepage for role-based routing
        setRedirectTo("/");
        setMessage(
          "Your email has been verified successfully! Redirecting to your dashboard..."
        );
        setTimeout(() => {
          try {
            navigate("/");
          } catch (navError) {
            console.error("Navigation error:", navError);
            window.location.href = "/";
          }
        }, 3000);
      } else {
        // User not authenticated, but verification succeeded
        // Check if they came from the verification required page
        const pendingEmail = localStorage.getItem("pendingVerificationEmail");

        if (pendingEmail) {
          // They came from EmailVerificationRequired page - redirect there with success
          setRedirectTo("/verify-email-required");
          setMessage(
            "Your email has been verified successfully! You can now log in to your account."
          );
          setTimeout(() => {
            try {
              navigate("/verify-email-required", {
                state: {
                  email: pendingEmail,
                  verified: true,
                },
              });
            } catch (navError) {
              console.error("Navigation error:", navError);
              window.location.href = "/login";
            }
          }, 2000);
        } else {
          // Standard case - redirect to login
          setRedirectTo("/login");
          setMessage(
            "Your email has been verified successfully! Please log in to access your account."
          );
          setTimeout(() => {
            try {
              navigate("/login");
            } catch (navError) {
              console.error("Navigation error:", navError);
              window.location.href = "/login";
            }
          }, 3000);
        }
      }
    } catch (redirectError) {
      // Errors in redirect logic should not affect verification status
      console.error(
        "Redirect error (verification still successful):",
        redirectError
      );
      // Keep success status and show a fallback message
      setMessage(
        "Your email has been verified successfully! Please close this tab and try logging in again."
      );
    }
  };

  const handleResendVerification = async () => {
    if (!user?.id) {
      setMessage("Please log in to resend verification email.");
      return;
    }

    setIsResending(true);
    try {
      // The verification token is generated, stored, and emailed entirely
      // server-side by the edge function (it holds the service key). The browser
      // never receives a token, so it can't be replayed to verify an account.
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/send-verification-email`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: user.email,
            username: user.user_metadata?.username || "user",
            action: "verification",
          }),
        }
      );

      if (response.ok) {
        setMessage(
          "A new verification email has been sent to your email address."
        );
      } else {
        const emailData = await response.json().catch(() => ({}));
        console.error("Failed to send verification email:", emailData.error);
        setMessage("Failed to send verification email. Please try again.");
      }
    } catch (error) {
      console.error("Resend error:", error);
      setMessage("An error occurred. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case "verifying":
        return <Loader2 className="w-16 h-16 text-orange-600 animate-spin" />;
      case "success":
        return <CheckCircle className="w-16 h-16 text-green-600" />;
      case "error":
      case "expired":
        return <XCircle className="w-16 h-16 text-red-600" />;
      default:
        return <Mail className="w-16 h-16 text-gray-600" />;
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case "verifying":
        return "Verifying Your Email...";
      case "success":
        return "Email Verified Successfully!";
      case "error":
        return "Verification Failed";
      case "expired":
        return "Verification Link Expired";
      default:
        return "Email Verification";
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "success":
        return "from-green-600 via-emerald-600 to-teal-600";
      case "error":
      case "expired":
        return "from-red-600 via-rose-600 to-pink-600";
      default:
        return "from-blue-600 via-purple-600 to-indigo-600";
    }
  };

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center relative overflow-hidden"
    >
      {/* Grid background with center glow effect */}
      <div className="absolute inset-0">
        {/* Base grid pattern */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
          }}
        />

        {/* Radial gradient mask to create center glow and edge fade effect */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(circle at 50% 50%, 
                rgba(184, 70, 14, 0.15) 0%, 
                rgba(184, 70, 14, 0.08) 40%, 
                rgba(184, 70, 14, 0.03) 70%,
                rgba(184, 70, 14, 0) 100%
              )
            `,
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
            maskImage: `
              radial-gradient(circle at 50% 50%, 
                rgba(255,255,255,1) 0%, 
                rgba(255,255,255,0.8) 30%, 
                rgba(255,255,255,0.3) 60%,
                rgba(255,255,255,0.1) 80%,
                rgba(255,255,255,0) 100%
              )
            `,
            WebkitMaskImage: `
              radial-gradient(circle at 50% 50%, 
                rgba(255,255,255,1) 0%, 
                rgba(255,255,255,0.8) 30%, 
                rgba(255,255,255,0.3) 60%,
                rgba(255,255,255,0.1) 80%,
                rgba(255,255,255,0) 100%
              )
            `,
          }}
        />

        {/* Interactive grid overlay for hover effects */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: gridDimensions.rows }).map((_, row) =>
            Array.from({ length: gridDimensions.cols }).map((_, col) => (
              <div
                key={`${row}-${col}`}
                className="absolute w-10 h-10 pointer-events-auto transition-all duration-300"
                style={{
                  left: `${col * 40}px`,
                  top: `${row * 40}px`,
                  animation: "none",
                }}
                onMouseEnter={(e) => {
                  gsap.fromTo(
                    e.target,
                    {
                      backgroundColor: "rgba(184, 70, 14, 0)",
                      boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                      scale: 1,
                    },
                    {
                      backgroundColor: "rgba(184, 70, 14, 0.3)",
                      boxShadow:
                        "0 0 25px rgba(184, 70, 14, 0.8), inset 0 0 15px rgba(184, 70, 14, 0.3)",
                      scale: 1.05,
                      duration: 0.2,
                      ease: "power2.out",
                    }
                  );

                  const adjacentSquares = document.querySelectorAll(
                    `[data-row="${row}"][data-col="${
                      col - 1
                    }"], [data-row="${row}"][data-col="${
                      col + 1
                    }"], [data-row="${
                      row - 1
                    }"][data-col="${col}"], [data-row="${
                      row + 1
                    }"][data-col="${col}"]`
                  );
                  adjacentSquares.forEach((square, index) => {
                    gsap.fromTo(
                      square,
                      {
                        backgroundColor: "rgba(184, 70, 14, 0)",
                        boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                      },
                      {
                        backgroundColor: "rgba(184, 70, 14, 0.1)",
                        boxShadow: "0 0 10px rgba(184, 70, 14, 0.4)",
                        duration: 0.15,
                        delay: index * 0.05,
                        ease: "power2.out",
                        onComplete: () => {
                          gsap.to(square, {
                            backgroundColor: "rgba(184, 70, 14, 0)",
                            boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                            duration: 0.3,
                            ease: "power2.out",
                          });
                        },
                      }
                    );
                  });
                }}
                onMouseLeave={(e) => {
                  gsap.to(e.target, {
                    backgroundColor: "rgba(184, 70, 14, 0)",
                    boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                    scale: 1,
                    duration: 0.4,
                    ease: "power2.out",
                  });
                }}
                data-row={row}
                data-col={col}
              />
            ))
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-md mx-auto p-6">
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 text-center">
          {/* Status Icon */}
          <div ref={iconRef} className="mb-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#B8460E] via-[#8B3A0E] to-[#B8460E] flex items-center justify-center shadow-2xl">
              {getStatusIcon()}
            </div>
          </div>

          {/* Content */}
          <div ref={contentRef}>
            <h1 className="text-2xl font-bold text-white mb-4">
              {getStatusTitle()}
            </h1>

            <p className="text-gray-300 text-sm leading-relaxed mb-6">
              {message}
            </p>

            {/* Action Buttons */}
            {status === "error" && (
              <div className="space-y-3">
                <button
                  onClick={handleResendVerification}
                  disabled={isResending}
                  className="w-full bg-gradient-to-r from-[#B8460E] via-[#8B3A0E] to-[#B8460E] hover:from-[#D4541A] hover:via-[#B8460E] hover:to-[#D4541A] disabled:opacity-50 text-white font-medium py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl"
                >
                  {isResending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Resend Verification Email
                    </>
                  )}
                </button>

                <button
                  onClick={() => navigate("/login")}
                  className="w-full bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white font-medium py-3 px-6 rounded-xl transition-all duration-200 border border-white/20"
                >
                  Back to Login
                </button>
              </div>
            )}

            {status === "success" && (
              <div className="bg-green-900/20 border border-green-500/50 rounded-xl p-4 mb-4">
                <p className="text-green-300 text-sm">
                  Redirecting you to{" "}
                  {redirectTo === "/" ? "your dashboard" : "login"}...
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-gray-400 text-xs">
            Need help?{" "}
            <a
              href="mailto:support@example.com"
              className="text-[#B8460E] hover:text-[#D4541A] transition-colors"
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
